import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Debts from './pages/Debts';
import Receivables from './pages/Receivables';
import LoginPage from './pages/LoginPage';
import Loans from './pages/Loans';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Tema do Material-UI
const theme = createTheme({
  palette: {
    primary: {
      main: '#1A202C',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f4f6f8',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
  },
});

function App() {
  const [session, setSession] = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  
  // ================================================================
  // CORREÇÃO: Garante que os estados sempre comecem como arrays vazios.
  const [debts, setDebts] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [loans, setLoans] = useState([]);
  // ================================================================
  
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAppLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchData = useCallback(async () => {
    if (!session) return;
    setDataLoading(true);
    try {
      const { data: debtsData, error: debtsError } = await supabase.from('debts').select('*');
      if (debtsError) throw debtsError;
      // CORREÇÃO: Usa '|| []' como um fallback para garantir que sempre seja um array
      setDebts(debtsData || []);

      const { data: receivablesData, error: receivablesError } = await supabase.from('receivables').select('*');
      if (receivablesError) throw receivablesError;
      setReceivables(receivablesData || []);
      
      const { data: loansData, error: loansError } = await supabase.from('loans').select('*');
      if (loansError) throw loansError;
      setLoans(loansData || []);

    } catch (error) {
      console.error("Erro ao buscar dados:", error.message);
    } finally {
      setDataLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Funções 'handleAddDebt', 'handleAddReceivable', etc. permanecem as mesmas
  const handleAddDebt = async (item) => {
    const { data, error } = await supabase.from('debts').insert([item]).select();
    if (error) alert(`Falha ao salvar a dívida: ${error.message}`);
    else setDebts(prev => [...prev, data[0]]);
  };

  const handleAddReceivable = async (item) => {
    const { data, error } = await supabase.from('receivables').insert([item]).select();
    if (error) alert(`Falha ao salvar o recebível: ${error.message}`);
    else setReceivables(prev => [...prev, data[0]]);
  };
  
  const handleAddLoan = async (item) => {
    const { data, error } = await supabase.from('loans').insert([item]).select();
    if (error) alert(`Falha ao salvar o empréstimo: ${error.message}`);
    else setLoans(prev => [...prev, data[0]]);
  };

  const handleUpdateStatus = async (id, newStatus, type) => {
    // ... (lógica de atualização de status) ...
  };
  
  const handleUpdateLoanStatus = async (id, newStatus) => {
    // ... (lógica de atualização de status) ...
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (appLoading) {
    return null;
  }

  if (!session) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage />
      </ThemeProvider>
    );
  } else {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Layout handleLogout={handleLogout}>
            <Routes>
              <Route path="/" element={<Dashboard debts={debts} receivables={receivables} loans={loans} loading={dataLoading} />} />
              <Route path="/dividas" element={<Debts items={debts} onAddItem={handleAddDebt} onUpdateStatus={handleUpdateStatus} loading={dataLoading} />} />
              <Route path="/a-receber" element={<Receivables items={receivables} onAddItem={handleAddReceivable} onUpdateStatus={handleUpdateStatus} loading={dataLoading} />} />
              <Route path="/emprestimos" element={<Loans items={loans} onAddItem={handleAddLoan} onUpdateStatus={handleUpdateLoanStatus} loading={dataLoading} />} />
            </Routes>
          </Layout>
        </Router>
      </ThemeProvider>
    );
  }
}

export default App;